import { UserType, UserCohortType, UserGroupType } from "@shared/User";
import { EventType } from "@shared/Event";
import { AnnouncementType } from "@shared/Announcement";
import { CertSettingType, CertSettingTypeDefaults } from "@shared/Certificate";
import { ActivityType } from "@shared/Activity";
import { useAlertContext } from './AlertContext';
import {useState, useEffect, createContext, useContext, ReactNode } from "react";
import api from "./Axios";
import { auth } from "./FirebaseInit";
import { dayjsConfigured } from "@shared/dateFormatter";
import devLog from "./DevLog";
import { CohortType, CohortTypeDefaults } from "@shared/Cohort";
import { designTokens } from "../theme/theme";

export interface AuthContextType {
  user: UserType | null;
  setUser: React.Dispatch<React.SetStateAction<UserType | null>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  users: UserType[] | null;
  setUsers: React.Dispatch<React.SetStateAction<UserType[] | null>>;
  events: EventType[] | null;
  setEvents: React.Dispatch<React.SetStateAction<EventType[] | null>>;
  announcements: AnnouncementType[] | null;
  setAnnouncements: React.Dispatch<React.SetStateAction<AnnouncementType[] | null>>;
  detailViewID: string | null;
  setDetailViewID: React.Dispatch<React.SetStateAction<string | null>>;
  certReqs: CertSettingType;
  setCertReqs: React.Dispatch<React.SetStateAction<CertSettingType>>;
  usersLoading: boolean;
  eventsLoading: boolean;
  announcementsLoading: boolean;
  filters: {users: string, events: string, announcements: string};
  setFilters: React.Dispatch<React.SetStateAction<{users: string, events: string, announcements: string}>>;
  sharedCohorts: string[];
  setSharedCohorts: React.Dispatch<React.SetStateAction<string[]>>;
  isTutorial: boolean;
  setIsTutorial: React.Dispatch<React.SetStateAction<boolean>>;
  viewMode: 'peer' | 'facilitator' | 'admin';
  setViewMode: React.Dispatch<React.SetStateAction<'peer' | 'facilitator' | 'admin'>>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { setAlert } = useAlertContext();
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<UserType[] | null>(null);
  const [events, setEvents] = useState<EventType[] | null>(null);
  const [announcements, setAnnouncements] = useState<AnnouncementType[] | null>(null);
  const [detailViewID, setDetailViewID] = useState<string | null>(null);
  const [certReqs, setCertReqs] = useState<CertSettingType>(CertSettingTypeDefaults);
  const [usersLoading, setUsersLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [certReqsLoading, setCertReqsLoading] = useState(false);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [filters, setFilters] = useState<{users: string, events: string, announcements: string}>(
    {
      users: 'confirmed',
      events: 'future',
      announcements: 'all'
    }
  );
  const [sharedCohorts, setSharedCohorts] = useState<string[]>([]);
  const [isTutorial, setIsTutorial] = useState(false);
  const [viewMode, setViewMode] = useState<'peer' | 'facilitator' | 'admin'>('peer');

  // handle loading
  useEffect(() => {
    if (eventsLoading || announcementsLoading || usersLoading || certReqsLoading) {
      setLoading(true);
    }
    else {
      setLoading(false);
    }
  }, [eventsLoading, announcementsLoading, usersLoading, certReqsLoading]);

  // handle tutorial
  useEffect(() => {
    if (user?.tutorial === false) {
      setIsTutorial(true);
    }
  }, [user?.tutorial]);

  // get user info
  useEffect(() => {
    setLoading(true);
    
    const unsubscribe = auth.onAuthStateChanged((auth_user) => {
      devLog({ message: 'AuthContext: auth onAuthStateChanged', type: 'log', optionalParams: [auth_user] });
      
      if (!auth_user) {
        devLog({ message: 'AuthContext: no user account or not logged in', type: 'info' });
        setUser(null);
        setLoading(false);
        return;
      }

      if (!user) {
        const fetchUser = async () => {
          api.get("/api/shared/user", {
            params: { 
              id: auth_user.uid
            }
          })
          .then((user_doc) => {
            if (!user_doc.data) {
              // no user created yet in database during signup
              setUser(null);
              setLoading(false);
              return;
            }

            const user_data = user_doc.data;
            const time = dayjsConfigured();
            let view_mode = user_data.role === 'facilitator' || user_data.role === 'admin' || user_data.role === 'editor' ? 'facilitator' : 'peer';
            if (user_data.current_group === 'facilitators' && user_data.confirm === false) {
              view_mode = 'peer';
            }
            setViewMode(view_mode as 'peer' | 'facilitator' | 'admin');
            
            if (!user_data.id || !user_data.last_login) {
              devLog({ message: 'AuthContext: auth user data issue', type: 'error', optionalParams: [user_data] });
              setAlert({open: true, message: 'There was a user authentication issue.', type: 'error'});
              setLoading(false);
              return;
            }

            api.put("/api/shared/user_info", {
              id: user_data.id,
              key: 'last_login',
              value: user_data.last_login
            })
            .then(() => {
              if (user_data.role === 'admin') {
                api.get("/api/admin/groups")
                .then((groups) => {
                  devLog({ message: 'AuthContext: groups', type: 'info', optionalParams: [groups.data] });

                  if (!groups.data || groups.data.length === 0) {
                    setAlert({open: true, message: 'No groups found', type: 'error'});
                    setLoading(false);
                    return;
                  }
                  user_data.group = groups.data;
                  let cohorts: UserCohortType[] = [];
                  user_data.group.forEach((group: {name: string, id: string, cohorts: CohortType[]}) => {
                    if (group.cohorts) {
                      group.cohorts.forEach((cohort, idx) => {
                        cohort.group = group.id;
                        cohorts.push(cohort);
                      })
                    }
                    else {
                      setAlert({open: true, message: 'No cohorts found for group ' + group.name, type: 'error'});
                      const temp_cohort: CohortType = {...CohortTypeDefaults, group: group.id, group_name: group.name, id: group.id + '_1', name: 'none'};
                      cohorts.push(temp_cohort);
                    }
                  })
                  user_data.cohort = cohorts;
                  setUser(user_data);
                  setLoading(false);
                })
                .catch ((err) => {
                  devLog({ message: 'AuthContext: Error getting groups', type: 'error', optionalParams: [err] });
                  setAlert({open: true, message: 'Error getting groups', type: 'error'});
                })
              }
              else if (user_data.role === 'facilitator' || user_data.role === 'editor') {
                api.get("/api/facilitator/cohorts", {
                  params: {
                    groups: user_data.group_list
                  }
                })
                .then((cohorts) => {
                  devLog({ message: 'AuthContext: cohorts', type: 'info', optionalParams: [cohorts.data] });

                  if (!cohorts.data || cohorts.data.length === 0) {
                    setAlert({open: true, message: 'No cohorts found', type: 'error'});
                    setLoading(false);
                    return;
                  }
                  user_data.cohort = cohorts.data;
                  let cohort_list = cohorts.data.map((cohort: UserCohortType) => {
                    return cohort.id;
                  });
                  user_data.cohort_list = cohort_list;
                  setUser(user_data);
                  setLoading(false);
                })
              }
              else {
                setUser(user_data);
                setLoading(false);
              }
            })
            .catch ((err) => {
              devLog({ message: 'AuthContext: Error getting user info', type: 'error', optionalParams: [err] });
              setAlert({open: true, message: 'Error getting user info', type: 'error'});
            });
          })
          .catch ((err) => {
            // cannot get user info from server even though user is verified
            devLog({ message: 'AuthContext: Error getting user info', type: 'error', optionalParams: [err] });
            setLoading(false);
            //setAlert({open: true, message: 'There was a user authentication issue.', type: 'error'});
          });
        }

        if (auth_user.metadata.creationTime === auth_user.metadata.lastSignInTime) {
          setTimeout(fetchUser, 1000);
        } else {
          fetchUser();
        }
      }
      else {
        // user !== null
        devLog({ message: 'AuthContext: user !== null', type: 'info', optionalParams: [user] });
        setLoading(false);
      }
      //} else {
        // no verified email (email is sent via login page)
      //  setLoading(false);
      //}
  });

    return () => unsubscribe();
  }, []);

  // get shared cohorts
  useEffect(() => {
    const fetchSharedCohorts = async () => {
      api.get("/api/user/shared_cohorts", {
        params: {
          cohort: user?.current_cohort,
          group: user?.current_group
        }
      })
      .then((cohorts) => {
        devLog({ message: 'AuthContext: shared_cohorts', type: 'info', optionalParams: [cohorts.data] });

        if (user?.alumni_community && !cohorts.data.cohorts.includes('alumni')) {
          setSharedCohorts([...cohorts.data.cohorts, 'alumni']);
        }
        else {
          setSharedCohorts(cohorts.data.cohorts);
        }
        setFilters({...filters, users: cohorts.data.user_filter});
      })
      .catch((err) => {
        devLog({ message: 'AuthContext: Error getting shared cohorts', type: 'error', optionalParams: [err] });
        setAlert({open: true, message: 'Error getting cohorts', type: 'error'});
        })
    }

    if (user?.current_cohort && user?.current_group) {
      fetchSharedCohorts();
    }
  }, [user?.current_cohort, user?.current_group, user?.alumni_community]);

  // facilitator: get users
  useEffect(() => {
    const fetchUsers = async () => {
      setUsersLoading(true);
      
      if (!user?.current_cohort || !user?.current_group) {
        setAlert({open: true, message: 'Error fetching users', type: 'error'});
        setUsers([]);
        setUsersLoading(false);
        return;
      }

      if (user?.current_group === 'facilitators') {
        setUsersLoading(false);
        return;
      }

      api.get("/api/facilitator/users", {
        params: {
          cohort: user.current_cohort
        }
      })
      .then((users_data_raw) => {
        let users_data = users_data_raw.data;
        if (users_data_raw.data.length === 0) {
          setUsers([]);
        } else {
          users_data = users_data
            .filter((x: UserType) => x.role === 'peer')
            .sort((a: UserType, b: UserType) => 
              a.last_name.toLowerCase().localeCompare(b.last_name.toLowerCase())
            );
          setUsers(users_data);
          devLog({ message: 'AuthContext: users', type: 'info', optionalParams: [users_data] });
        }
      })
      .catch((err) => {
        devLog({ message: 'AuthContext: Error in fetchUsers', type: 'error', optionalParams: [err] });
        setAlert({open: true, message: 'Error fetching data', type: 'error'});
        setUsers([]);
      })
      .finally(() => {
        setUsersLoading(false);
      });
    };
    
    if (user && (user.role === 'facilitator' || user.role === 'admin' || user.role === 'editor')) {
      fetchUsers();
    }
  }, [user?.current_cohort, user?.current_group]);

  // facilitator: get events
  useEffect(() => {
    const fetchEvents = async () => {
      setEventsLoading(true);

      if (user?.current_group === 'facilitators') {
        setEventsLoading(false);
        return;
      }

      api.get("/api/user/events", {
        params: {
          shared_cohorts: sharedCohorts
        }
      })
      .then((cohort_events) => {
        devLog({ message: 'AuthContext: cohort_events', type: 'info', optionalParams: [cohort_events?.data] });
        if (cohort_events?.data.length > 0) {
          const now = new Date();
          const events = cohort_events?.data.map((event: EventType) => ({
            ...event,
            color: event.type === 'deadline' ? designTokens.colors.background.yellow :
              new Date(event.start_date).getTime() > now.getTime() ? designTokens.colors.success.main : designTokens.colors.background.lightGrey
          }));
          events.sort((a: EventType, b: EventType) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
          setEvents(events);
        }
        else {
          setEvents([]);
        }
      })
      .catch((err) => {
        devLog({ message: 'AuthContext: Error fetching events', type: 'error', optionalParams: [err] });
        setAlert({open: true, message: 'Error getting events', type: 'error'});
        setEvents([]);
      })
      .finally(() => {
        setEventsLoading(false);
      });
    }

    if (user && sharedCohorts.length > 0 && (user.role === 'facilitator' || user.role === 'admin' || user.role === 'editor')) {
      if (!user?.current_cohort || !user?.current_group) {
        setAlert({open: true, message: 'Error fetching events', type: 'error'});
        setEvents([]);
        return;
      }
      else {
        fetchEvents();
      }
    }
  }, [user?.current_cohort, user?.current_group, sharedCohorts]);

  // get announcements
  useEffect(() => {
    const fetchAnnouncements = async () => {
      setAnnouncementsLoading(true);
      if (!user?.current_cohort || !user?.current_group) {
        setAlert({open: true, message: 'Error fetching announcements', type: 'error'});
        setAnnouncements([]);
        return;
      }

      if (user.current_group === 'facilitators') {
        // Load sandbox data
        api.get("/api/user/sandbox_data")
        .then((sandboxData) => {
          setUsers(sandboxData.data.users);
          setEvents(sandboxData.data.events.map((event: EventType) => ({
            ...event,
            color: event.type === 'deadline' ? designTokens.colors.background.yellow :
              new Date(event.start_date).getTime() > new Date().getTime() ? designTokens.colors.success.main : designTokens.colors.background.lightGrey
          })).sort((a: EventType, b: EventType) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()));
          const now = new Date();
          const visibleAnnouncements = sandboxData.data.announcements.filter((item: AnnouncementType) => 
            (item.display === 'manual' && item.visible) ||
            (item.display === 'timed' && 
             new Date(item.start_date).getTime() <= now.getTime() && 
             new Date(item.end_date).getTime() >= now.getTime())
          );
          setAnnouncements(visibleAnnouncements);
          if (!user?.partner || user?.partner.length === 0) {
            const user_temp = {
              ...user,
              partner: sandboxData.data.partners
            };
            console.log('AuthContext: setUser', sandboxData.data.partners);
            setUser(user_temp);
          }
        })
        .catch((err) => {
          devLog({ message: 'AuthContext: Error loading sandbox data', type: 'error', optionalParams: [err] });
          setAlert({open: true, message: 'Error loading sandbox data', type: 'error'});
          setUsers([]);
        })
        .finally(() => {
          setAnnouncementsLoading(false);
        });
        return;
      }

      api.get("/api/user/announcements", {
        params: {
          shared_cohorts: sharedCohorts
        }
      })
      .then((response) => {
        if (user.role === 'peer') {
          const now = new Date();
          const visibleAnnouncements = response.data.filter((item: AnnouncementType) => 
            (item.display === 'manual' && item.visible) ||
            (item.display === 'timed' && 
             new Date(item.start_date).getTime() <= now.getTime() && 
             new Date(item.end_date).getTime() >= now.getTime())
          );
          setAnnouncements(visibleAnnouncements);
        } else {
          setAnnouncements(response.data);
        }
      })
      .catch((err) => {
        devLog({ message: 'AuthContext: Error fetching announcements', type: 'error', optionalParams: [err] });
        setAlert({open: true, message: 'Error getting announcements', type: 'error'});
        setAnnouncements([]);
      })
      .finally(() => {
        setAnnouncementsLoading(false);
      });
    }
    
    if (user && sharedCohorts.length > 0) {
      fetchAnnouncements();
    }
  }, [user?.current_cohort, user?.current_group, sharedCohorts]);

  // peer: get events
  useEffect(() => {
    const fetchEvents = async () => {
      setEventsLoading(true);

      if (user?.current_group === 'facilitators') {
        setEventsLoading(false);
        return;
      }

      api.get("/api/user/events", {
        params: {
          shared_cohorts: sharedCohorts
        }
      })
      .then(async (res_events) => {
        let event_data = res_events.data;

        api.get("/api/shared/activities", {
          params: {
            id: user?.id
          }
        })
        .then((res_activities) => {
          let activity_data: ActivityType[] = res_activities.data;

          const now = new Date();

          event_data.forEach((event: EventType) => {
            if (event.type === 'deadline') event.color = designTokens.colors.background.yellow;
            else event.color = designTokens.colors.background.lightGrey;
          })

          activity_data.forEach((activity: ActivityType) => {
            event_data.forEach((event: EventType) => {
              if (activity.id === event.id) {
                event.status = activity.status;
                if (activity.status === "attended") {
                  event.color = designTokens.colors.primary.main;
                }
                else if (activity.status === "registered" && new Date(event.end_date).getTime() + (24 * 60 * 60 * 1000) < now.getTime()) {
                  event.color = designTokens.colors.error.main;
                  event.status = "absent";
                }
                else if (activity.status === "registered" && new Date(event.start_date).getTime() < now.getTime() && new Date(event.end_date).getTime() + (60 * 60 * 1000) > now.getTime() ) {
                  event.color = designTokens.colors.warning.main;
                }
                else if (activity.status === "registered") {
                  event.color = designTokens.colors.success.main;
                }
              }
            })
          })

          event_data = event_data.filter((item: EventType) => new Date(item.end_date).getTime() > now.getTime() || 'status' in item );

          event_data.sort((a: EventType, b: EventType) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
          setEvents(event_data);
        })
        .catch((err) => {
          setAlert({open: true, message: 'Problem parsing events', type: 'error'});
        })
        .finally(() => {
          setEventsLoading(false);
        });
      })
      .catch((err) => {
        setAlert({open: true, message: 'Problem getting events', type: 'error'});
      })
    }

    if (user && user.role === 'peer' && sharedCohorts.length > 0) {
      if (!user.current_cohort || !user.current_group || !user.id) {
        setAlert({open: true, message: 'Error fetching events', type: 'error'});
        setEvents([]);
        return;
      }
      else {
        fetchEvents();
      }
    };

  }, [user?.current_cohort, user?.current_group, user?.id, sharedCohorts]);

  // get cert_req
  useEffect(() => {
    const fetchCertReqs = async () => {
      setCertReqsLoading(true);
      api.get("/api/user/setting", {
        params: {
          group: user?.current_group,
          setting: 'certificate'
        }
      })
      .then((res) => {
        if (!res.data || !res.data.settings) {
          setAlert({open: true, message: 'Problem loading certificate requirements', type: 'error'});
          return;
        }
        let temp_cert = res.data;
        setCertReqs(temp_cert.settings);
      })
      .catch ((err) => {
        setAlert({open: true, message: 'Error getting certificate requirements', type: 'error'});
      })
      .finally(() => {
        setCertReqsLoading(false);
      });
    };
    
    if (user) {
      if (!user.current_group) {
        setAlert({open: true, message: 'Error fetching certificate requirements', type: 'error'});
        return;
      }
      else {
        fetchCertReqs();
      }
    }
  }, [user?.current_group]);

  const value: AuthContextType = {
    user, setUser,
    users, setUsers,
    events, setEvents,
    announcements, setAnnouncements,
    loading, setLoading,
    certReqs, setCertReqs,
    detailViewID, setDetailViewID,
    usersLoading,
    eventsLoading,
    announcementsLoading,
    filters, setFilters,
    sharedCohorts, setSharedCohorts,
    isTutorial, setIsTutorial,
    viewMode, setViewMode
  };

  return (
    <AuthContext.Provider value = {value} >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};